import { useCallback, useEffect, useState } from 'react';
import { log } from '../../app/logger';
import { loadRoster } from '../../app/roster';
import { EMPTY_TEAM_SHAPE, loadTeamShape } from '../../app/settingsB';
import { renderTeamProfile, teamProfileFacts } from '../../domain/teamProfile';
import type { TeamProfileFacts } from '../../domain/teamProfile';
import type { OurPlayerRow, TeamShape } from '../../types';

/**
 * B spec §2.4 — the My Team tab's controller (build unit B-6), `useHomeB`'s shape kept: one
 * read on mount, an exposed `refresh` for every write's return path, and a completed-reads
 * counter rendered as `data-read` so a browser pass waits on a number instead of a sleep.
 *
 * *"Committed is not rendered"* is this project's most repeated defect shape — every write on
 * the My Team screen calls {@link RosterActions.refresh} after its dispatch resolves, so what
 * the user reads is always a re-read of what the engine holds, never the screen's belief about
 * what its own write did.
 */

const MODULE = 'ui-b/myteam';

type Phase = 'loading' | 'ready' | 'failed';

export interface RosterState {
  readonly phase: Phase;
  /** The whole roster in the facade's stable jersey-ascending order. */
  readonly players: readonly OurPlayerRow[];
  /** §3.7.38's team shape, parsed defensively — a corrupt value reads as the empty shape. */
  readonly shape: TeamShape;
  /** §17.1's freshness line + §18.5's count, from the one derivation both read. */
  readonly facts: TeamProfileFacts;
  /**
   * §17.1's *"What Coach Claude reads"* — **the actual rendered prose**, produced by calling the
   * same `renderTeamProfile` the payload calls, so the preview cannot drift from what leaves the
   * device. `null` when there is nothing to send.
   */
  readonly preview: string | null;
  /** Completed reads — incremented after the rows it describes (the `useHomeB` device). */
  readonly reads: number;
}

export interface RosterActions {
  readonly refresh: () => void;
}

export function useRoster() {
  const [phase, setPhase] = useState<Phase>('loading');
  const [players, setPlayers] = useState<readonly OurPlayerRow[]>([]);
  const [shape, setShape] = useState<TeamShape>(EMPTY_TEAM_SHAPE);
  const [reads, setReads] = useState(0);

  const refresh = useCallback((): void => {
    void (async () => {
      // Two reads, one await, so `data-read` never increments over a half-updated screen.
      const [rows, teamShape] = await Promise.all([loadRoster(), loadTeamShape()]);
      setPlayers(rows);
      setShape(teamShape);
      setPhase('ready');
      setReads((count) => count + 1);
    })().catch((cause: unknown) => {
      log.error(MODULE, 'B §2.4: the team profile could not be read', {
        message: cause instanceof Error ? cause.message : String(cause),
      });
      setPhase('failed');
    });
  }, []);

  useEffect(refresh, [refresh]);

  /**
   * The active roster is what travels (§17.1's *"not sent to Coach Claude"*), so the preview and
   * the facts are both computed over that filter and never over the whole list — a preview that
   * showed inactive players would be a promise the payload does not keep.
   */
  const active = players.filter((player) => player.active);

  return {
    state: {
      phase,
      players,
      shape,
      facts: teamProfileFacts(players, shape),
      preview: renderTeamProfile(active, shape),
      reads,
    } satisfies RosterState,
    actions: { refresh } satisfies RosterActions,
  };
}
